<template>
  <div class="scrolling-touch no-scrollbar overflow-x-auto border-y bg-white px-4 py-2 md:px-6 lg:px-8">
    <div class="md:flex md:items-center md:justify-between md:space-x-6">
      <div class="border-r border-gray-300 lg:pr-6 pr-4">
        <button
          class="py-2 pr-4 pl-3 text-sm font-semibold border border-peacock-200 rounded-sm whitespace-nowrap bg-peacock-25 flex items-center gap-2 outline-none focus-visible:ring focus-visible:ring-peacock-300 focus-visible:ring-offset-2"
          @click="showQuickSearch = true"
        >
          <YscIconsLighthouse class="h-5 w-5" />
          {{ $t('components.app_search.find_a_vehicle_btn_label') }}
        </button>
      </div>
      <div class="flex grow flex-nowrap gap-3 lg:gap-4 py-2">
        <!-- PRICE -->
        <YscDropdownFilter
          :is-active="pricesCount > 0"
          :open="openFilter === FILTERS_MAP.PRICES"
          @toggle-open="onToggleFilter(FILTERS_MAP.PRICES)"
          @close="onSubmitFilter({ form: formPrices })"
        >
          <template #label>
            <YscIconsCoupon class="h-4 w-4 mr-2 hidden lg:block" />
            {{ priceLabel }}
          </template>
          <form
            id="search-form-prices"
            class="w-80"
            @submit.prevent="onSubmitFilter({ form: formPrices })"
          >
            <AppSearchFilterFormSection>
              <template #title>
                <div class="flex items-center justify-between font-semibold">
                  <h5>
                    {{ $t('components.app_search.filters.price_per_day_max') }}
                  </h5>
                  <span>{{ $n(coercedMaxPrice, currency) }}</span>
                </div>
              </template>
              <div class="w-full">
                <YscInputSlider
                  v-model="coercedMaxPrice"
                  :min="PRICE_RANGE.MIN"
                  :max="PRICE_RANGE.MAX"
                  :step="5"
                />
              </div>
            </AppSearchFilterFormSection>
            <AppSearchFilterFormSection :title="$t('components.app_search.filters.discounts')">
              <YscInputCheckbox
                id="discount_early"
                v-model="formPrices.discount_early"
                name="discount_early"
                is-block
                class="flex-row-reverse justify-between !space-x-0 w-full"
              >
                <div class="flex flex-row gap-3 items-center">
                  <YscIconsDiscount
                    aria-hidden="true"
                    class="h-5 w-5"
                  />
                  {{ $t('components.app_price_form_early.title') }}
                </div>
              </YscInputCheckbox>
              <YscInputCheckbox
                id="discount_weekly"
                v-model="formPrices.discount_weekly"
                name="discount_weekly"
                is-block
                class="flex-row-reverse justify-between !space-x-0 w-full"
              >
                <div class="flex flex-row gap-3 items-center">
                  <YscIconsCoupon
                    aria-hidden="true"
                    class="h-5 w-5"
                  />
                  {{ $t('components.app_price_form_discount.title') }}
                </div>
              </YscInputCheckbox>
            </AppSearchFilterFormSection>
          </form>

          <AppSearchFilterFooter
            form-id="search-form-prices"
            @clear="onClearFilter({ form: formPrices })"
          />
        </YscDropdownFilter>

        <!-- SEATS -->
        <YscDropdownFilter
          :is-active="formPassengers.seatbelts !== null"
          :open="openFilter === FILTERS_MAP.PASSENGERS"
          @toggle-open="onToggleFilter(FILTERS_MAP.PASSENGERS)"
          @close="onSubmitFilter({ form: formPassengers })"
        >
          <template #label>
            <YscIconsFamily class="h-4 w-4 mr-2 hidden lg:block" />
            {{ formPassengerLabel }}
          </template>

          <AppSearchFiltersTravellers
            v-model="formPassengers"
            v-model:show-advanced-passengers="showAdvancedPassengers"
            :is-crossed-search="isCrossedResults"
            @submit="onSubmitFilter({ form: formPassengers })"
          />
          <AppSearchFilterFooter
            form-id="search-form-passengers"
            @clear="onClearFilter({ form: formPassengers })"
          />
        </YscDropdownFilter>

        <!-- VEHICLE TYPES -->
        <YscDropdownFilter
          :is-active="formTypes.types.length > 0"
          :open="openFilter === FILTERS_MAP.TYPES"
          @toggle-open="onToggleFilter(FILTERS_MAP.TYPES)"
          @close="onSubmitFilter({ form: formTypes })"
        >
          <template #label>
            <YscIconsVehicle class="h-4 w-4 mr-2 hidden lg:block" />
            {{
              formTypes.types.length
                ? $t('components.app_search.filters.labels.vehicle_types_plural', {
                  count_number: $n(formTypes.types.length),
                }, formTypes.types.length)
                : $t('components.app_search.filters.vehicle_type')
            }}
          </template>

          <AppSearchFilterTypes
            v-model="formTypes"
            @submit="onSubmitFilter({ form: formTypes })"
          />

          <AppSearchFilterFooter
            form-id="search-form-types"
            @clear="onClearFilter({ form: formTypes })"
          />
        </YscDropdownFilter>

        <!-- EQUIPMENTS -->
        <YscDropdownFilter
          :is-active="formEquipments.equipments.length > 0"
          :open="openFilter === FILTERS_MAP.EQUIPMENTS"
          :slot-anchor="filterNeedAnchor.equipments ? 'right-2' : null"
          @toggle-open="onToggleFilter(FILTERS_MAP.EQUIPMENTS, $event)"
          @close="onSubmitFilter({ form: formEquipments })"
        >
          <template #label>
            <YscIconsShower class="h-4 w-4 mr-2 hidden lg:block" />
            {{
              formEquipments.equipments.length
                ? $t('components.app_search.filters.labels.equipments_plural', {
                  count_number: $n(formEquipments.equipments.length),
                }, formEquipments.equipments.length)
                : $t('commons.equipments')
            }}
          </template>
          <AppSearchFiltersEquipments
            v-model="formEquipments.equipments"
            :is-crossed-search="isCrossedResults"
            @submit="onSubmitFilter({ form: { ...formEquipments, ...formPassengers } })"
          />

          <AppSearchFilterFooter
            form-id="search-form-equipments"
            @clear="onClearFilter({ form: formEquipments })"
          />
        </YscDropdownFilter>

        <!-- PREFERENCES -->
        <YscDropdownFilter
          class="pr-4"
          :is-active="preferencesCount > 0"
          :open="openFilter === FILTERS_MAP.PREFERENCES"
          :slot-anchor="filterNeedAnchor.preferences ? 'right-2' : null"
          @toggle-open="onToggleFilter(FILTERS_MAP.PREFERENCES, $event)"
          @close="onSubmitFilter({ form: formPreferences })"
        >
          <template #label>
            <YscIconsPreferencesUnselected class="h-4 w-4 mr-2 hidden lg:block" />
            {{
              preferencesCount
                ? $t('components.app_search.filters.labels.preferences_plural', { count_number: $n(preferencesCount) }, preferencesCount)
                : $t('components.app_search.filters.preferences')
            }}
          </template>

          <AppSearchFilterPreferences
            v-model="formPreferences"
            v-model:vehicle-smoking-disallowed="vehicleSmokingDisallowed"
            :is-crossed-search="isCrossedResults"
            @submit="onSubmitFilter({ form: formPreferences })"
          />
          <AppSearchFilterFooter
            form-id="search-form-preferences"
            @clear="onClearFilter({ form: formPreferences })"
          />
        </YscDropdownFilter>
      </div>

      <div class="hidden lg:block">
        <YscInputSwitch
          id="ho-search-filter-show-map`"
          v-model="renderMap"
          reverse
          class="text-sm"
        >
          <template #default>
            {{ $t('components.app_search.map_toggle') }}
          </template>
        </YscInputSwitch>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { RefType } from '~/types/commons'

const { t, n } = useI18n()

const { renderMap, filters, currentFilter, showQuickSearch, isCrossedResults } = storeToRefs(useSearchStore())

const { map: currencyReferentialMap } = useReferentialMap('currency')

const { formPrices, initFormPrices, coercedMaxPrice } = useSearchFiltersPricesForm()
const { formTypes, initFormTypes } = useSearchFiltersFormTypes()
const { formPassengers, initFormPassengers, showAdvancedPassengers } = useSearchFiltersFormPassengers()
const { formEquipments, initFormEquipements } = useSearchFiltersFormEquipments()
const { formPreferences, vehicleSmokingDisallowed, initFormPreferences } = useSearchFiltersPreferencesForm()

const filterNeedAnchor = ref({
  equipments: false,
  preferences: false,
})

const openFilter = ref<string | null>(null)

const MAX_FILTER_WIDTH = 384

const FILTERS_MAP = {
  PRICES: 'prices',
  TYPES: 'types',
  PASSENGERS: 'passengers',
  EQUIPMENTS: 'equipments',
  PREFERENCES: 'preferences',
}

const PRICE_RANGE = { MIN: 0, MAX: 500 }

const {
  public: {
    currency: { default: defaultCurrency },
  },
} = useRuntimeConfig()

// @ts-expect-error this is valid as long as defaultCurrency is a valid currency value
const currency = computed(() => currencyReferentialMap.value[defaultCurrency])

const priceLabel = computed(() => {
  const min_price = filters.value.min_price
  const max_price = filters.value.max_price

  if (min_price) {
    if (max_price) {
      return `${n(min_price, currency.value)} - ${n(max_price, currency.value)}`
    }
    return t('components.app_search.filters.from_price_dynamic', { price_string: n(min_price, currency.value) })
  }
  else if (max_price) {
    return t('components.app_search.filters.to_price_dynamic', { price_string: n(max_price, currency.value) })
  }

  return t('components.app_search.filters.price')
})

const initForms = () => {
  initFormPrices()
  initFormTypes()
  initFormPassengers()
  initFormEquipements()
  initFormPreferences()
}

const updateOpenFilter = (filter: string | null) => {
  currentFilter.value = filter
}

watch(filters, () => {
  initForms()
}, {
  deep: true,
})

watch(openFilter, (newValue) => {
  updateOpenFilter(newValue)
})

const closeFilter = () => {
  openFilter.value = null
}

const onToggleFilter = (filter: string, evt: MouseEvent | undefined = undefined) => {
  if (openFilter.value === filter) {
    closeFilter()
    return
  }

  if (evt && evt.target) {
    // @ts-expect-error not sure how to make TS recognize getBoundingClientRect
    const willBeOffScreen = window.innerWidth - evt.target.getBoundingClientRect().left < MAX_FILTER_WIDTH

    const filterCouldNeedAnchor = (f: string): f is keyof RefType<typeof filterNeedAnchor> => {
      return ['preferences', 'equipments'].includes(f)
    }

    if (filterCouldNeedAnchor(filter)) {
      filterNeedAnchor.value[filter] = willBeOffScreen
    }
  }
  openFilter.value = filter
}

const onClearFilter = ({ form }: { form: Partial<SearchFilters> }) => {
  const initialFilters = initialSearchFiltersFactory()
  for (const key of Object.keys(form)) {
    // @ts-expect-error We know key is in initialFilter and that form & initialFilter have the same shape
    form[key] = initialFilters[key]
    // @ts-expect-error We know key is in initialFilter and that filters & initialFilter have the same shape
    filters.value[key] = initialFilters[key]
  }
}

const { navigateToFilters } = useSearchNavigateToFilters()
const onSubmitFilter = async ({ form }: { form: Partial<SearchFilters> }) => {
  filters.value = {
    ...filters.value,
    ...form,
  }
  closeFilter()
  await navigateToFilters()
}

initForms()

const pricesCount = useSearchFiltersCount(formPrices)
const preferencesCount = useSearchFiltersCount(formPreferences)

const formPassengerLabel = computed(() => formPassengers.value.seatbelts
  ? t('components.app_search.filters.labels.passengers_plural', {
      count_number: n(formPassengers.value.seatbelts),
    }, formPassengers.value.seatbelts)
  : t('commons.travelers'))
</script>
